import { Moon, Sun, Wallet, LogOut, Menu, User as UserIcon, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { CURRENCIES, useCurrency } from "@/lib/currency";
import { useI18n, type Locale } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";

export function TopBar({ onMenu, hideLogo }: { onMenu?: () => void; hideLogo?: boolean }) {
  const { theme, toggle } = useTheme();
  const { locale, setLocale, t } = useI18n();
  const { currency, setCurrency, format } = useCurrency();
  const { user, logout } = useAuth();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-border bg-background/70 px-4 backdrop-blur-xl">
      {onMenu && (
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onMenu}
          aria-label={t("a11y.open_menu")}
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}
      {!hideLogo && (
        <Link
          to="/dashboard"
          className="flex items-center gap-2 font-bold tracking-tight hover:opacity-80 transition-opacity mr-2"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary shadow-glow">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-base font-bold text-foreground">SmsEmpire</span>
        </Link>
      )}
      <div className="flex-1" />

      {user && (
        <div className="flex items-center gap-2">
          <Link to="/recharge" className="hidden sm:flex">
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full border-border bg-card px-3 shadow-elegant hover:bg-accent"
            >
              {t("topup")}
            </Button>
          </Link>
          <Link to="/recharge" className="sm:hidden">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full border-border bg-card shadow-elegant hover:bg-accent"
              aria-label={t("topup")}
            >
              <Wallet className="h-4 w-4" aria-hidden />
            </Button>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 rounded-full border border-border bg-card px-2 sm:px-3 py-1.5 text-xs sm:text-sm shadow-elegant">
            <Wallet className="h-4 w-4 text-primary hidden sm:block" />
            <span className="text-muted-foreground hidden sm:block">{t("balance")}:</span>
            <span className="font-semibold">{format(user.balance)}</span>
          </div>
          <Link
            to="/profile"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
          >
            <UserIcon className="h-4 w-4" />
          </Link>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 font-medium uppercase">
            {locale}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("topbar.language")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {(["en", "es", "fr", "de", "it", "pt", "zh", "ru"] as Locale[]).map((l) => (
            <DropdownMenuItem key={l} onClick={() => setLocale(l)}>
              {l === "en"
                ? "English"
                : l === "es"
                  ? "Español"
                  : l === "fr"
                    ? "Français"
                    : l === "de"
                      ? "Deutsch"
                      : l === "it"
                        ? "Italiano"
                        : l === "pt"
                          ? "Português"
                          : l === "zh"
                            ? "中文"
                            : "Русский"}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 font-medium">
            {currency}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("topbar.currency")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {CURRENCIES.map((c) => (
            <DropdownMenuItem key={c} onClick={() => setCurrency(c)}>
              {c}
            </DropdownMenuItem>
          ))}
          {currency !== "USD" && (
            <>
              <DropdownMenuSeparator />
              {/* Payments are always taken in USD; anything else is a display
                  conversion. Saying so here avoids a surprise at the card form. */}
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {t("currency.usd_notice")}
              </p>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button variant="ghost" size="icon" onClick={toggle} aria-label={t("topbar.toggle_theme")}>
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      {user && (
        <Button variant="ghost" size="icon" onClick={logout} aria-label={t("nav.logout")}>
          <LogOut className="h-5 w-5" />
        </Button>
      )}
    </header>
  );
}
